import {
  AccountTicketDetails,
  BankPriceType,
  EventDetails,
  PlanPackageComponentDetails,
  SimplePlanPackageDetails,
  SubscriptionDetails,
  TicketCartDetails,
} from "features/types";
import uniqBy from "lodash/uniqBy";
import { formatCurrency } from "utils/appUtils";
import { getPlanType } from "utils/planPackageUtils";

export const sortSubscriptionsByPrimaryMember = (
  matchedSubscriptions: Array<SubscriptionDetails>
) => {
  matchedSubscriptions.sort((a) => {
    if (a.member.type === "PRIMARY") {
      return -1;
    }
    return 0;
  });
};

export const getComponentsInSubscriptions = (
  component: PlanPackageComponentDetails,
  subscriptions: SubscriptionDetails[]
) => {
  const results: PlanPackageComponentDetails[] = [];

  for (let sub of subscriptions) {
    if (sub.expired) continue;
    for (let comp of sub.planPackage.components) {
      if (comp.id === component.id) {
        results.push(comp);
      }
    }
  }

  return results;
};

export function componentNotExceedingPerEventMax({
  component,
  accountEvents = [],
  selectedEvent,
  isChangingSeats = false,
  keptOriginalTicketIds = [],
}: {
  component: PlanPackageComponentDetails;
  accountEvents?: AccountTicketDetails[];
  selectedEvent: EventDetails;
  isChangingSeats?: boolean;
  keptOriginalTicketIds?: number[];
}) {
  const componentPerEventMax = component.perEventMax;

  if (componentPerEventMax < 0) return true; // component has unlimited per event max

  const ticketsWithComponentUsed = accountEvents
    .filter((accountEvent) => accountEvent.event.id === selectedEvent.id)
    .reduce((result: number, accountTicket) => {
      const componentUsedForEvent = accountTicket.tickets.filter((ticket) => {
        if (ticket.planPackageComponentId !== component.id) return false;
        // when changing seats, only count tickets being kept (they still occupy a slot)
        if (isChangingSeats) return keptOriginalTicketIds.includes(ticket.id);
        return true;
      });
      return componentUsedForEvent.length + result;
    }, 0);

  return componentPerEventMax - ticketsWithComponentUsed > 0;
}

// calculates the remaining available slots for a component
// by components * per event max - components redeemed
export function getRemainingPerEventMaxForComponent({
  component,
  subscriptions,
  eventId,
  events = [],
  isChangingSeats,
  originalEventId,
  maxSeatsAllowed,
  groupCode,
  keptOriginalTicketIds = [],
}: {
  component: PlanPackageComponentDetails;
  subscriptions: SubscriptionDetails[];
  events?: AccountTicketDetails[];
  eventId: number;
  isChangingSeats: boolean;
  originalEventId?: number;
  maxSeatsAllowed: number;
  groupCode?: string;
  keptOriginalTicketIds?: number[];
}) {
  if (component.perEventMax === -1) return maxSeatsAllowed;

  const components = subscriptions
    .filter(
      (sub) =>
        sub.planPackage.components.some((comp) => comp.id === component.id) &&
        !sub.expired
    )
    .map((sub) => sub.planPackage.primaryComponent);

  const maxPerEventMax = components.length * component.perEventMax;
  let counter = maxPerEventMax;

  events.forEach((event) => {
    for (const ticket of event.tickets) {
      if (originalEventId === event.event.id) continue;
      if (
        ticket.planPackageComponentId === component.id &&
        event.event.id === eventId
      ) {
        // if isChangingSeats is true, only skip tickets belonging to the group being changed
        // that are NOT being kept — kept tickets still consume their perEventMax slot
        if (
          isChangingSeats &&
          event.groupCode?.groupCode === groupCode &&
          !keptOriginalTicketIds.includes(ticket.id)
        ) {
          continue;
        }
        counter--;
      }
    }
  });

  return counter;
}

// Returns a set that contains user ids who are not eligible to reserve , initialized to reservation user's count
// @param matchedSubs:Array, list of all reservation subs in the account
// @param eventId:Integer, current selected event's id
// @param events:Array, list of all events that the account has tickets for
export function usersNotGoing({
  subscriptions,
  eventId,
  events = [],
  isChangingSeats = false,
  groupCode,
}: {
  subscriptions: SubscriptionDetails[];
  eventId: any;
  events?: AccountTicketDetails[];
  isChangingSeats?: boolean;
  groupCode?: string | null;
}): Set<number> {
  const userSet = new Set<number>();
  if (subscriptions.length === 0) return userSet;
  // current selected event's id
  // 1. minus users that have ticket for this event
  // If changingSeat is true, skip this step
  const event = events.filter((accountEvent) => {
    return (
      accountEvent.event.id === parseInt(eventId) &&
      accountEvent.groupCode.groupCode !== groupCode
    );
  });
  if (event.length > 0) {
    event.forEach(({ tickets }) => {
      tickets.forEach((ticket) => {
        if (!ticket.user) return; // only check reservation tickets
        subscriptions.forEach((sub) => {
          if (
            ticket.planPackageComponentId ===
            sub.planPackage.primaryComponent.id
          ) {
            userSet.add(ticket.user!.id); // if user already has ticket for this event, add user to the set
          }
        });
      });
    });
  }

  // 2. minus users that are out of slots
  const maxSlotPerUser = subscriptions[0].planPackage.primaryComponent.quantity;

  subscriptions.forEach((sub) => {
    if (!isChangingSeats && sub.currentTickets === maxSlotPerUser) {
      userSet.add(sub.member.user.id);
    }
  });
  // 3. minus users that have expired reservation plan
  subscriptions.forEach((sub) => {
    if (sub.expired) {
      userSet.add(sub.member.user.id);
    }
  });
  return userSet;
}

export const findComponentById = (
  id: number,
  userGoingSubscriptions: SubscriptionDetails[]
) =>
  userGoingSubscriptions[0].planPackage.components.find(
    (component) => component.id === id
  );

export const findUserById = (
  id: number,
  userGoingSubscriptions: SubscriptionDetails[]
) =>
  userGoingSubscriptions.find((sub) => sub.member.user.id === id)?.member.user;

export const isPrimaryComponent = (
  userOrComponentId: number,
  subscription: SubscriptionDetails
) => {
  return subscription.planPackage.primaryComponent.id === userOrComponentId;
};

export const getRemainingQuantityForComponent = ({
  component,
  subscriptions,
  events = [],
  isChangingSeats,
  originalEventId,
  eventId,
  maxSeatsAllowed,
  groupCode,
  keptOriginalTicketIds = [],
}: {
  component: PlanPackageComponentDetails;
  subscriptions: SubscriptionDetails[];
  events?: AccountTicketDetails[];
  isChangingSeats: boolean;
  eventId: number; // if user is upgrading seats
  originalEventId?: number; // if it is defined, then user is switching event
  maxSeatsAllowed: number;
  groupCode?: string;
  keptOriginalTicketIds?: number[];
}) => {
  if (component.quantity === -1 || component.type === "BANK") {
    return maxSeatsAllowed;
  }

  if (component.type === "VOUCHER") {
    const totalAvailableVouchers = subscriptions.reduce((result, sub) => {
      return result + sub.availableVouchers;
    }, 0);
    if (!isChangingSeats) return totalAvailableVouchers;
    // If changing seats/exchanging event
    let vouchersUsed = 0;
    for (const event of events) {
      for (const ticket of event.tickets) {
        if (ticket.planPackageComponentId !== component.id) continue;

        // if user is changing event, add back all vouchers used for the original event
        if (originalEventId && originalEventId === ticket.event.id) {
          vouchersUsed++;
        } else if (
          ticket.event.id === eventId &&
          event.groupCode?.groupCode === groupCode &&
          !keptOriginalTicketIds.includes(ticket.id)
        ) {
          // add back vouchers only for tickets being released (not kept)
          vouchersUsed++;
        }
      }
    }
    return totalAvailableVouchers + vouchersUsed;
  }

  // component.type === 'RESERVATION'
  const totalComponents = getComponentsInSubscriptions(
    component,
    subscriptions
  );
  const totalQuantity = totalComponents.length * component.quantity;

  let usage = 0;

  for (const event of events) {
    for (const ticket of event.tickets) {
      // If user is changing seats or switching event, don't count towards usage
      if (isChangingSeats || originalEventId) {
        continue;
      }
      if (ticket.planPackageComponentId === component.id) {
        usage++;
      }
    }
  }
  return totalQuantity - usage;
};

export const getRemainingAvailableSlotsForComponent = ({
  component,
  subscriptions,
  eventId,
  events = [],
  isChangingSeats,
  originalEventId,
  maxSeatsAllowed,
  groupCode,
  keptOriginalTicketIds = [],
}: {
  component: PlanPackageComponentDetails;
  subscriptions: SubscriptionDetails[];
  events?: AccountTicketDetails[];
  eventId: number;
  originalEventId?: number;
  isChangingSeats: boolean;
  maxSeatsAllowed: number;
  groupCode?: string;
  keptOriginalTicketIds?: number[];
}) => {
  const remainingPerEventMax = getRemainingPerEventMaxForComponent({
    component,
    subscriptions,
    events,
    eventId,
    isChangingSeats,
    originalEventId,
    maxSeatsAllowed,
    groupCode,
    keptOriginalTicketIds,
  });
  const remainingQuantity = getRemainingQuantityForComponent({
    component,
    subscriptions,
    events,
    isChangingSeats,
    originalEventId,
    eventId,
    maxSeatsAllowed,
    groupCode,
    keptOriginalTicketIds,
  });
  console.log("remainging per event max", remainingPerEventMax);
  console.log("remainging quantity", remainingQuantity);

  return Math.min(remainingPerEventMax, remainingQuantity);
};

export const getRemainingSlotsForPlanPackage = ({
  planPackage,
  subscriptions,
  eventId,
  events = [],
  isChangingSeats,
  originalEventId,
  maxSeatsAllowed,
  groupCode,
  keptOriginalTicketIds = [],
}: {
  planPackage: SimplePlanPackageDetails;
  subscriptions: SubscriptionDetails[];
  events?: AccountTicketDetails[];
  eventId: number;
  isChangingSeats: boolean;
  originalEventId?: number;
  maxSeatsAllowed: number;
  groupCode?: string;
  keptOriginalTicketIds?: number[];
}) => {
  const guestComponents = subscriptions
    .map((sub) => sub.planPackage.components)
    .flat(1)
    .filter((component) => component.type.includes("GUEST"));
  const uniqGuestComponents = uniqBy(guestComponents, "id");
  const planType = getPlanType(subscriptions[0].planPackage);
  let selectedPlanType: PlanPackageComponentDetails["type"];
  let partySize = 0;
  let partySizePlusGuest = 0;
  let defaultSelectedPartySize = 0;
  const remainingSlotsForComponent = getRemainingAvailableSlotsForComponent({
    component: planPackage.primaryComponent,
    subscriptions,
    events,
    eventId,
    isChangingSeats,
    originalEventId,
    maxSeatsAllowed,
    groupCode,
    keptOriginalTicketIds,
  });
  console.log("remaining slots for component", remainingSlotsForComponent);

  if (planType.includes("VOUCHER")) {
    selectedPlanType = "VOUCHER";
  } else if (planType.includes("RESERVATION")) {
    selectedPlanType = "RESERVATION";
  } else {
    selectedPlanType = "BANK";
  }

  if (selectedPlanType === "RESERVATION" && !isChangingSeats) {
    // We want to filter out users who already have ticket for this event, and only count the remaining reservation slots
    const usersNotGoingSet = usersNotGoing({
      subscriptions,
      eventId,
      events,
    });
    partySize = subscriptions.length - usersNotGoingSet.size;
  } else {
    partySize = remainingSlotsForComponent;
  }

  let totalGuestCount = 0;
  for (const component of uniqGuestComponents) {
    const remainingQuantityForComponent = getRemainingQuantityForComponent({
      component,
      subscriptions,
      events: events || [],
      isChangingSeats,
      originalEventId,
      eventId,
      maxSeatsAllowed,
      groupCode,
      keptOriginalTicketIds,
    });

    const remainingPerEventMaxForComponent =
      getRemainingPerEventMaxForComponent({
        component,
        subscriptions: subscriptions,
        events,
        eventId,
        isChangingSeats,
        originalEventId,
        maxSeatsAllowed,
        groupCode,
        keptOriginalTicketIds,
      });

    const availableSlots = Math.min(
      remainingQuantityForComponent,
      remainingPerEventMaxForComponent
    );
    totalGuestCount += availableSlots;
  }
  partySizePlusGuest = partySize + totalGuestCount;
  // Default to one seat per subscription on the plan, capped by the remaining
  // slots (remaining vouchers for voucher plans). Voucher plans used to default
  // to min(remaining, 2) regardless of subscription count.
  defaultSelectedPartySize = Math.min(partySize, subscriptions.length);
  return {
    selectedPlanType,
    partySize,
    totalGuestCount,
    partySizePlusGuest,
    defaultSelectedPartySize,
  };
};

export const getAllItemsPrice = (
  charge: TicketCartDetails,
  priceType: BankPriceType
) => {
  const allPrice = charge.items.reduce((acc, item) => {
    return acc + Number(item.totalPrice);
  }, 0);
  return formatCurrency(allPrice, { priceType });
};
